import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { confirmDialog } from "primereact/confirmdialog";
import axios from "axios";

const ProductMultiBranchList = () => {
  const toast = useRef(null);

  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [branchProducts, setBranchProducts] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation');
      setClients(
        res.data.map((c) => ({ label: c.client_name, value: c.client_id }))
      );
    } catch {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load clients",
      });
    }
  };

  const fetchCategories = async (clientId) => {
    try {
      const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/get_asgcategories', { params: { clientid: clientId } });
      setCategories(
        res.data.map((c) => ({
          label: c.category_name,
          value: c.category_master_id,
        }))
      );
    } catch {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load categories",
      });
    }
  };

  const fetchProducts = async (clientId, categoryId) => {
    try {
      const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/get_client_products', { params: { clientid: clientId, category_master_id: categoryId } });
      setProducts(
        res.data.map((p) => ({
          label: p.product_name,
          value: p.product_code,
        }))
      );
    } catch {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load products",
      });
    }
  };

  const fetchBranchProducts = async (productId, clientId) => {
    try {
      const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/get_product_by_branches', { params: { product_id: productId, clientid: clientId } });
      setBranchProducts(res.data);
    } catch {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load product details",
      });
    }
  };

  const confirmSave = () => {
    confirmDialog({
      message: "Are you sure you want to save all changes?",
      header: "Confirm Save",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-success",
      accept: () => saveAllChanges(),
      reject: () => {
        toast.current.show({
          severity: "info",
          summary: "Cancelled",
          detail: "Save operation cancelled",
          life: 2000,
        });
      },
    });
  };

  //Save
  const saveAllChanges = async () => {
    if (!branchProducts.length) return;

    try {
      const res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/update_branch_products_batch', { products: branchProducts }, { headers: { 'Content-Type': 'application/json' } });

      if (res.data.status === "success") {
        toast.current.show({
          severity: "success",
          summary: "Saved",
          detail: res.data.message,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res.data.message,
        });
      }
    } catch {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save all changes",
      });
    }
  };

  // Editors
  const priceEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        mode="decimal"
        useGrouping={false}
        onValueChange={(e) => {
          const updated = [...branchProducts];
          const idx = updated.findIndex(
            (i) => i.branch_id === options.rowData.branch_id
          );

          const newTotal = e.value || 0;
          const taxRate = parseFloat(updated[idx].basic_tax_percent || 0);
          const newBasic = parseFloat(
            (newTotal / (1 + taxRate / 100)).toFixed(2)
          );
          const taxAmount = newTotal - newBasic;

          updated[idx] = {
            ...updated[idx],
            price: newTotal,
            basic_rate: newBasic,
            basic_tax_amount: parseFloat(taxAmount.toFixed(2)),
          };

          setBranchProducts(updated);
          options.editorCallback(e.value);
        }}
      />
    );
  };

  const taxEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        mode="decimal"
        useGrouping={false}
        minFractionDigits={0}
        maxFractionDigits={0}
        onValueChange={(e) => {
          const updated = [...branchProducts];
          const idx = updated.findIndex(
            (i) => i.branch_id === options.rowData.branch_id
          );

          const newTax = e.value || 0;
          const basicRate = parseFloat(updated[idx].basic_rate || 0);
          const taxAmount = (basicRate * newTax) / 100;
          const total = basicRate + taxAmount;

          updated[idx] = {
            ...updated[idx],
            basic_tax_percent: newTax,
            price: parseFloat(total.toFixed(0)),
            basic_tax_amount: parseFloat(taxAmount.toFixed(2)),
          };

          setBranchProducts(updated);
          options.editorCallback(e.value);
        }}
      />
    );
  };

  return (
    <div className="p-4">
      <ConfirmDialog />
      <Toast ref={toast} />
      <h2 className="text-xl font-bold mb-3">Product List by Branch</h2>

      <div className="card mb-4 flex flex-wrap gap-3">
        <Dropdown
          value={selectedClient}
          options={clients}
          onChange={(e) => {
            setSelectedClient(e.value);
            setSelectedCategory(null);
            setSelectedProduct(null);
            setBranchProducts([]);
            fetchCategories(e.value);
          }}
          placeholder="Select Client"
          className="w-full md:w-3"
          filter
        />

        <Dropdown
          value={selectedCategory}
          options={categories}
          onChange={(e) => {
            setSelectedCategory(e.value);
            setSelectedProduct(null);
            setBranchProducts([]);
            fetchProducts(selectedClient, e.value);
          }}
          placeholder="Select Category"
          className="w-full md:w-3"
          filter
          disabled={!selectedClient}
        />

        <Dropdown
          value={selectedProduct}
          options={products}
          onChange={(e) => {
            setSelectedProduct(e.value);
            fetchBranchProducts(e.value, selectedClient);
          }}
          placeholder="Select Product"
          className="w-full md:w-3"
          filter
          disabled={!selectedCategory}
        />
      </div>

      <div className="card">
        <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
          <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
            <span className="p-input-icon-left w-full">
              <i className="pi pi-search"></i>
              <InputText
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search..."
                className="p-inputtext-sm w-full"
              />
            </span>
          </div>
        </div>

        <DataTable
          value={branchProducts}
          showGridlines
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          tableStyle={{ minWidth: "50rem" }}
          className="p-datatable-sm"
          globalFilter={globalFilter}
          editMode="cell"
        >
          <Column field="branch_name" header="Branch" />

          <Column
            field="price"
            header={
              <span>
                Total Price <i className="pi pi-pencil" />
              </span>
            }
            body={(row) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <span>{row.price}</span>
                <i
                  className="pi pi-pencil"
                  style={{ fontSize: "0.9rem", color: "#666" }}
                />
              </div>
            )}
            editor={(options) => priceEditor(options)}
          />

          <Column
            field="basic_tax_percent"
            header={
              <span>
                Tax (%) <i className="pi pi-pencil" />
              </span>
            }
            body={(row) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <span>{row.basic_tax_percent}</span>
                <i
                  className="pi pi-pencil"
                  style={{ fontSize: "0.9rem", color: "#666" }}
                />
              </div>
            )}
            editor={(options) => taxEditor(options)}
          />

          <Column
            field="basic_rate"
            header="Basic Rate"
            body={(row) => parseFloat(row.basic_rate || 0).toFixed(2)}
          />

          <Column
            field="basic_tax_amount"
            header="Tax Amount"
            body={(row) => parseFloat(row.basic_tax_amount || 0).toFixed(2)}
          />
        </DataTable>

        {branchProducts.length > 0 && (
        <div className="mt-4 flex justify-content-end">
            <Button
                label="Save Changes"
                icon="pi pi-save"
                className="p-button-success"
                onClick={confirmSave}
            />
        </div>
        )}
      </div>
    </div>
  );
};

export default ProductMultiBranchList;
